import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2 } from "lucide-react";
import ItemCombobox from "./ItemCombobox";

export default function ReturnInventoryDialog({ open, onOpenChange, project, onSuccess }) {
  const [itemId, setItemId] = useState("");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Reshape the project's on-site aggregation into the shape ItemCombobox expects
  const onsiteItems = (project?.onsite_inventory || []).map((i) => ({
    id: i.item_id || i._id,
    sku: i.sku || "",
    name: i.item_name,
    unit: "",
    quantity: i.net,
  })).filter((i) => i.id);

  useEffect(() => {
    if (open) {
      setItemId("");
      setQty(1);
      setNote("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!itemId) return toast.error("Select an item");
    setBusy(true);
    try {
      await api.post("/inventory/return", { item_id: itemId, project_id: project.id, quantity: Number(qty), note });
      toast.success("Inventory returned to warehouse");
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Return failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md" data-testid="return-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Return to Warehouse</DialogTitle>
        </DialogHeader>
        {onsiteItems.length === 0 ? (
          <div className="py-6 text-sm text-slate-600">No on-site inventory to return.</div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Item on site</Label>
              <div className="mt-1.5">
                <ItemCombobox
                  items={onsiteItems}
                  value={itemId}
                  onChange={setItemId}
                  testId="return-item"
                  placeholder="Type SKU or name…"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Quantity</Label>
              <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="return-qty-input" className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Note</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="return-note-input" className="rounded-sm mt-1.5" rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="return-submit-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
                {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
                Return
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
